// Admin — post editor (PRD §3.10). Create/edit, Save draft, Publish, Unpublish.
// Autosaves to draft once the post exists, so a flaky connection won't lose work.
// Block-based: text + images + video, all stored on R2 (no paid Cloudflare Stream).

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { api, ApiError } from "../../lib/api";
import type { Block, PostStatus } from "../../lib/types";
import { AppHeader } from "../../components/AppHeader";
import { BlockEditor } from "../../components/BlockEditor";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { uploadVideoThumbnail } from "../../lib/videoThumbnail";

type SaveState = "idle" | "saving" | "saved" | "error";

// ── Cover image picker ────────────────────────────────────────────────────────

interface CoverPickerProps {
  coverBlock: Block | null;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => void;
  uploading: boolean;
  uploadProgress: number;
  uploadError: string | null;
  disabled?: boolean;
}

function CoverPicker({ coverBlock, onUpload, onRemove, uploading, uploadProgress, uploadError, disabled }: CoverPickerProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const [imgError, setImgError] = useState(false);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImgError(false);
    void onUpload(file);
  }

  // Reset error state when cover changes
  useEffect(() => { setImgError(false); }, [coverBlock?.url]);

  if (uploading) {
    return (
      <div className="relative h-52 w-full overflow-hidden rounded-2xl border-2 border-citrus/40 bg-paper-deep sm:h-72">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6">
          <div className="h-2 w-full max-w-xs overflow-hidden rounded-full bg-ink/10">
            <div
              className="h-full rounded-full bg-citrus transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-sm font-semibold text-ink-soft">Uploading… {uploadProgress}%</p>
        </div>
      </div>
    );
  }

  if (coverBlock?.type === "image" && coverBlock.url) {
    return (
      <div className="relative w-full overflow-hidden rounded-2xl border-2 border-ink/10">
        {imgError ? (
          <div className="flex h-52 items-center justify-center bg-paper-deep sm:h-72">
            <div className="flex flex-col items-center gap-2 text-center">
              <span className="text-4xl">🖼️</span>
              <p className="text-sm text-ink-muted">Image couldn't load</p>
            </div>
          </div>
        ) : (
          <img
            src={coverBlock.url}
            alt="Cover image"
            className="block w-full"
            onError={() => setImgError(true)}
          />
        )}
        <div className="absolute bottom-3 right-3 flex gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={disabled}
            className="rounded-lg bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm hover:bg-black/80"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="rounded-lg bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm hover:bg-love/80"
          >
            Remove
          </button>
        </div>
        <div className="absolute left-3 top-3 rounded-md bg-black/60 px-2 py-1 text-xs font-semibold text-white/90">
          Cover image
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    );
  }

  if (coverBlock?.type === "video" && coverBlock.url) {
    return (
      <div className="relative w-full overflow-hidden rounded-2xl border-2 border-ink/10 bg-black">
        <video
          src={coverBlock.url}
          poster={coverBlock.thumbnailUrl ?? undefined}
          controls
          playsInline
          className="block max-h-[420px] w-full bg-black"
        />
        <div className="absolute left-3 top-3 rounded-md bg-black/60 px-2 py-1 text-xs font-semibold text-white/90">
          Cover video
        </div>
        <div className="absolute bottom-3 right-3">
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="rounded-lg bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm hover:bg-love/80"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {uploadError && (
        <p className="mb-2 rounded-lg border border-love/30 bg-love/5 px-3 py-2 text-sm text-love">{uploadError}</p>
      )}
      <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-ink/20 bg-paper-deep px-6 py-10 text-center sm:py-14">
        <span className="text-4xl">📸</span>
        <div>
          <p className="font-semibold text-ink">Add a cover image or video</p>
          <p className="mt-1 text-sm text-ink-muted">It appears at the top of your post — optional but recommended</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={disabled}
            className="flex items-center gap-1.5 rounded-xl border-2 border-ink px-4 py-2 text-sm font-semibold text-ink shadow-[2px_2px_0_0_var(--color-ink)] hover:bg-paper-edge active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
          >
            📷 Add photo
          </button>
          <button
            type="button"
            onClick={() => videoRef.current?.click()}
            disabled={disabled}
            className="flex items-center gap-1.5 rounded-xl border-2 border-ink/40 px-4 py-2 text-sm font-semibold text-ink-soft hover:border-ink hover:text-ink"
          >
            🎥 Add video
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

// ── Main PostEditor ───────────────────────────────────────────────────────────

export function PostEditor() {
  const { id } = useParams();
  const editing = id != null;
  const navigate = useNavigate();
  const { setPreviewPublic } = useAuth();
  useEffect(() => setPreviewPublic(false), [setPreviewPublic]);

  const [postId, setPostId] = useState<number | null>(editing ? Number(id) : null);
  const [title, setTitle] = useState("");
  // coverBlock = first image/video block; bodyBlocks = everything else
  const [coverBlock, setCoverBlock] = useState<Block | null>(null);
  const [bodyBlocks, setBodyBlocks] = useState<Block[]>([]);
  const [serverBodyBlocks, setServerBodyBlocks] = useState<Block[]>([]);
  const [hasPendingUploads, setHasPendingUploads] = useState(false);
  const [status, setStatus] = useState<PostStatus>("draft");
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [publishing, setPublishing] = useState(false);
  // Cover upload state
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [coverError, setCoverError] = useState<string | null>(null);
  const lastSaved = useRef({ title: "", blocksJson: "[]" });
  // Snapshot of the post exactly as it was when this edit session started, so
  // Cancel has something concrete to revert to (autosave means "just navigate
  // away" would silently keep whatever was typed).
  const original = useRef<{ title: string; blocks: Block[] } | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Split loaded blocks into cover + body
  function splitBlocks(blocks: Block[]): { cover: Block | null; body: Block[] } {
    const firstMedia = blocks.findIndex(b => b.type === "image" || b.type === "video");
    if (firstMedia === -1) return { cover: null, body: blocks };
    return {
      cover: blocks[firstMedia]!,
      body: blocks.filter((_, i) => i !== firstMedia),
    };
  }

  // Merge cover + body back to a single ordered array: cover first, then body
  function mergeBlocks(): Block[] {
    return coverBlock ? [coverBlock, ...bodyBlocks] : bodyBlocks;
  }

  useEffect(() => {
    if (!editing) return;
    const pid = Number(id);
    if (!Number.isInteger(pid)) {
      setError("That post has wandered off.");
      setLoading(false);
      return;
    }
    let alive = true;
    api
      .adminPost(pid)
      .then(({ post }) => {
        if (!alive) return;
        setPostId(post.id);
        setTitle(post.title);
        setStatus(post.status);
        const { cover, body } = splitBlocks(post.blocks);
        setCoverBlock(cover);
        setServerBodyBlocks(body);
        setBodyBlocks(body);
        lastSaved.current = { title: post.title, blocksJson: JSON.stringify(post.blocks) };
        original.current = { title: post.title, blocks: post.blocks };
      })
      .catch((err) => alive && setError(err instanceof ApiError ? err.message : "Couldn't load that post."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [editing, id]);

  // Autosave (debounced)
  useEffect(() => {
    if (postId === null || hasPendingUploads || coverUploading) return;
    const allBlocks = mergeBlocks();
    const blocksJson = JSON.stringify(allBlocks);
    if (title === lastSaved.current.title && blocksJson === lastSaved.current.blocksJson) return;
    setSaveState("saving");
    const t = setTimeout(async () => {
      try {
        const patch: { title?: string; blocks?: Block[] } = { blocks: allBlocks };
        if (title.trim()) patch.title = title.trim();
        await api.updatePost(postId, patch);
        lastSaved.current = { title, blocksJson };
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    }, 1400);
    return () => clearTimeout(t);
  }, [title, coverBlock, bodyBlocks, postId, hasPendingUploads, coverUploading]);

  const handleBodyBlocksChange = useCallback((blocks: Block[]) => setBodyBlocks(blocks), []);
  const handlePendingChange = useCallback((pending: boolean) => setHasPendingUploads(pending), []);

  async function handleCoverUpload(file: File) {
    setCoverError(null);
    setCoverUploading(true);
    setCoverProgress(0);
    try {
      if (file.type.startsWith("image/")) {
        const { url } = await api.uploadImage(file, (pct) => setCoverProgress(pct));
        setCoverBlock({ type: "image", url, text: null, streamUid: null, thumbnailUrl: null });
      } else if (file.type.startsWith("video/")) {
        // Cover clips go to R2 (same path as body videos) — no paid Stream needed.
        const [thumbnailUrl, { url }] = await Promise.all([
          uploadVideoThumbnail(file),
          api.uploadVideo(file, (pct) => setCoverProgress(pct)),
        ]);
        setCoverBlock({ type: "video", url, text: null, streamUid: null, thumbnailUrl });
      } else {
        setCoverError("Only images and videos are supported.");
      }
    } catch (err) {
      setCoverError(err instanceof ApiError ? err.message : "Upload failed — try again.");
    } finally {
      setCoverUploading(false);
    }
  }

  function handleCoverRemove() {
    setCoverBlock(null);
    setCoverError(null);
  }

  async function saveDraft(): Promise<number | null> {
    if (!title.trim()) {
      setError("Give your post a title first.");
      return null;
    }
    setError(null);
    setSaveState("saving");
    const allBlocks = mergeBlocks();
    try {
      if (postId === null) {
        const { post } = await api.createPost(title.trim(), allBlocks);
        setPostId(post.id);
        setStatus(post.status);
        lastSaved.current = { title: title.trim(), blocksJson: JSON.stringify(allBlocks) };
        setSaveState("saved");
        return post.id;
      }
      const { post } = await api.updatePost(postId, { title: title.trim(), blocks: allBlocks });
      setStatus(post.status);
      lastSaved.current = { title: title.trim(), blocksJson: JSON.stringify(allBlocks) };
      setSaveState("saved");
      return post.id;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save. Try again?");
      setSaveState("error");
      return null;
    }
  }

  async function publish() {
    const pid = await saveDraft();
    if (pid === null) return;
    setPublishing(true);
    try {
      await api.publishPost(pid);
      navigate(`/post/${pid}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't publish. Try again?");
      setPublishing(false);
    }
  }

  async function unpublish() {
    if (postId === null) return;
    try {
      await api.unpublishPost(postId);
      setStatus("draft");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't unpublish.");
    }
  }

  function hasUnsavedChanges(): boolean {
    if (!editing || !original.current) return false;
    return (
      title.trim() !== original.current.title ||
      JSON.stringify(mergeBlocks()) !== JSON.stringify(original.current.blocks)
    );
  }

  function onCancelClick() {
    if (hasUnsavedChanges()) {
      setShowCancelConfirm(true);
    } else {
      navigate("/admin");
    }
  }

  async function confirmDiscard() {
    if (postId !== null && original.current) {
      setCancelling(true);
      try {
        await api.updatePost(postId, { title: original.current.title, blocks: original.current.blocks });
      } catch {
        // Best-effort revert — don't let a flaky request trap the admin on this screen.
      } finally {
        setCancelling(false);
      }
    }
    navigate("/admin");
  }

  const isBusy = hasPendingUploads || coverUploading;

  const saveIndicator =
    isBusy
      ? "⏳ Uploading…"
      : saveState === "saving"
        ? "Saving…"
        : saveState === "saved"
          ? "✓ Saved"
          : saveState === "error"
            ? "⚠ Couldn't save"
            : status === "published"
              ? "Published 🍊"
              : "Draft";

  return (
    <div className="min-h-dvh bg-paper pb-36">
      <AppHeader />
      <main className="mx-auto w-full max-w-xl px-5 py-6">

        {/* Top nav */}
        <div className="flex items-center justify-between">
          <Link to="/admin" className="text-xs font-semibold text-ink-muted hover:text-ink">
            ← All posts
          </Link>
          <span
            className={
              "rounded-full px-2.5 py-1 text-xs font-semibold " +
              (saveState === "error"
                ? "bg-love/10 text-love"
                : status === "published"
                  ? "bg-citrus/10 text-citrus-deep"
                  : "bg-ink/10 text-ink-muted")
            }
          >
            {saveIndicator}
          </span>
        </div>

        <h1 className="mt-4 text-2xl font-bold text-ink">
          {editing || postId !== null ? "Edit post" : "New post"}
        </h1>

        {loading ? (
          <div className="flex justify-center py-20">
            <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-citrus/30 border-t-citrus" />
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-6">

            {/* Step 1: Cover */}
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-ink-muted">
                Step 1 — Cover image or video
              </p>
              <CoverPicker
                coverBlock={coverBlock}
                onUpload={handleCoverUpload}
                onRemove={handleCoverRemove}
                uploading={coverUploading}
                uploadProgress={coverProgress}
                uploadError={coverError}
                disabled={loading}
              />
            </div>

            {/* Step 2: Title */}
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-ink-muted">
                Step 2 — Title
              </p>
              <Field
                label=""
                name="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your post a title…"
                maxLength={140}
              />
            </div>

            {/* Step 3: Body */}
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-ink-muted">
                Step 3 — Your story
              </p>
              <p className="mb-3 text-sm text-ink-muted">
                Write, add more photos, or embed videos below.
              </p>
              <BlockEditor
                key={postId ?? "new"}
                initialBlocks={serverBodyBlocks}
                onChange={handleBodyBlocksChange}
                onPendingChange={handlePendingChange}
                disabled={loading}
              />
            </div>

            {error ? (
              <p className="rounded-xl border-2 border-love/30 bg-love/5 px-4 py-3 text-sm font-medium text-love">
                {error}
              </p>
            ) : null}
          </div>
        )}
      </main>

      {/* Sticky action bar */}
      {!loading ? (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t-2 border-ink/10 bg-paper/95 backdrop-blur-md">
          <div className="mx-auto w-full max-w-xl px-5 py-4">
            {status === "published" ? (
              <div className="flex gap-3">
                <Button variant="ghost" type="button" onClick={onCancelClick} disabled={isBusy}>
                  Cancel
                </Button>
                <Button variant="ghost" onClick={() => void unpublish()} className="flex-1">
                  Unpublish
                </Button>
                <Button
                  onClick={() => void saveDraft()}
                  className="flex-1"
                  loading={saveState === "saving"}
                  disabled={isBusy}
                >
                  {isBusy ? "Uploading…" : "Save changes"}
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button variant="ghost" type="button" onClick={onCancelClick} disabled={isBusy}>
                  Cancel
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => void saveDraft()}
                  className="flex-1"
                  loading={saveState === "saving"}
                  disabled={isBusy}
                >
                  Save draft
                </Button>
                <Button
                  loading={publishing}
                  disabled={isBusy}
                  onClick={() => void publish()}
                  className="flex-1"
                >
                  {isBusy ? "Uploading…" : "Publish 🍊"}
                </Button>
              </div>
            )}
            {isBusy && (
              <p className="mt-2 text-center text-xs text-ink-muted">
                Wait for uploads to finish before saving
              </p>
            )}
          </div>
        </div>
      ) : null}

      {showCancelConfirm ? (
        <CancelEditModal
          busy={cancelling}
          onKeepEditing={() => setShowCancelConfirm(false)}
          onDiscard={() => void confirmDiscard()}
        />
      ) : null}
    </div>
  );
}

function CancelEditModal({
  busy,
  onKeepEditing,
  onDiscard,
}: {
  busy: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onKeepEditing();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onKeepEditing, busy]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current && !busy) onKeepEditing();
      }}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-2xl border-2 border-ink/10 bg-paper shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
        <div className="px-5 py-5">
          <h2 className="text-lg font-bold text-ink">Discard your changes?</h2>
          <p className="mt-1 text-sm text-ink-muted">
            This post will go back to how it was before you started editing.
          </p>

          <div className="mt-5 flex gap-3">
            <Button variant="ghost" type="button" onClick={onKeepEditing} disabled={busy} className="flex-1">
              Keep editing
            </Button>
            <button
              type="button"
              onClick={onDiscard}
              disabled={busy}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-ink bg-love px-5 py-3 font-semibold text-white shadow-[3px_3px_0_0_var(--color-ink)] transition-transform active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60"
            >
              {busy ? "Discarding…" : "Discard changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
